import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./lib/i18n";
import TitleUpdater from "./components/TitleUpdater";
import AuthCallback from "./pages/AuthCallback";
import DriverLogin from "./pages/DriverLogin";
import DriverDashboard from "./pages/DriverDashboard";
import Home from "./pages/Home";
import Vehicles from "./pages/Vehicles";
import RoutesPage from "./pages/RoutesPage";
import Tours from "./pages/Tours";
import TourDetail from "./pages/TourDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import CustomBooking from "./pages/CustomBooking";
import AcceptQuote from "./pages/AcceptQuote";
import FAQ from "./pages/FAQ";
import BookingSearch from "./pages/BookingSearch";
import BookingForm from "./pages/BookingForm";
import BookingConfirmation from "./pages/BookingConfirmation";
import BookingLookup from "./pages/BookingLookup";
import Login from "./pages/Login";
import CustomerDashboard from "./pages/CustomerDashboard";
import CustomerSettings from "./pages/CustomerSettings";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminSeoPages from "./pages/admin/AdminSeoPages";
import SeoPage from "./pages/SeoPage";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminVehicles from "./pages/admin/AdminVehicles";
import AdminDrivers from "./pages/admin/AdminDrivers";
import AdminRoutes from "./pages/admin/AdminRoutes";
import AdminTours from "./pages/admin/AdminTours";
import AdminSlogans from "./pages/admin/AdminSlogans";
import AdminCalendar from "./pages/admin/AdminCalendar";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminLayout from "./components/AdminLayout";
import { getUser } from "./lib/api";

const RequireAdmin = ({ children }) => {
  const u = getUser();
  if (!u || u.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
};

const RequireCustomer = ({ children }) => {
  const u = getUser();
  if (!u) return <Navigate to="/login" replace />;
  return children;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const loc = useLocation();
  if (loc.hash && loc.hash.includes("session_id=")) return <AuthCallback />;
  return (<><TitleUpdater /><Routes>
        <Route path="/" element={<Home />} />
        <Route path="/vehicles" element={<Vehicles />} />
        <Route path="/routes" element={<RoutesPage />} />
        <Route path="/tours" element={<Tours />} />
        <Route path="/tours/:id" element={<TourDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/custom-booking" element={<CustomBooking />} />
        <Route path="/seo/:slug" element={<SeoPage />} />
        <Route path="/accept-quote/:bookingId" element={<AcceptQuote />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/book" element={<BookingSearch />} />
        <Route path="/book/form" element={<BookingForm />} />
        <Route path="/book/confirm/:bookingId" element={<BookingConfirmation />} />
        <Route path="/lookup" element={<BookingLookup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={<RequireCustomer><CustomerDashboard /></RequireCustomer>} />
        <Route path="/driver/login" element={<Login />} />
        <Route path="/driver" element={<DriverDashboard />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
          <Route index element={<AdminDashboard />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="seo" element={<AdminSeoPages />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="vehicles" element={<AdminVehicles />} />
          <Route path="drivers" element={<AdminDrivers />} />
          <Route path="routes" element={<AdminRoutes />} />
          <Route path="tours" element={<AdminTours />} />
          <Route path="slogans" element={<AdminSlogans />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="*" element={<div className="p-10 text-center text-slate-500" data-testid="admin-not-found"><div className="text-2xl font-display font-black text-[#1e1b4b] mb-2">Page not found</div><div className="text-sm">This admin section doesn't exist. Use the sidebar to navigate.</div></div>} />
        </Route>
      </Routes></>);
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AppRouter />
      </LanguageProvider>
    </BrowserRouter>
  );
}
